import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { ReactElement } from 'react';
import { Alert } from '../../modals/Alert';
import { AddDrawButton } from './buttons/AddDrawButton';
import { InspectionButton } from './buttons/InspectionButton';
import { ReceiptButton } from './buttons/ReceiptButton';
import { PostClosingButton } from './buttons/PostClosingButton';
import { DrawApi, IDrawBudget } from '@apply/api/src/api/DrawApi';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { DrawBudgetCategory } from './functions';
import { LoanType } from '@apply/constants';
import { ToggleButton, ToggleButtonLabelSize } from '@apply/components';
import { handleAdminSuccess } from '../../../../../../apps/admin/src/common/functions/handleAdminSuccess';
import { handleAdminError } from '../../../../../../apps/admin/src/common/functions/handleAdminError';

interface IDrawButtonGroup {
    drawBudget: IDrawBudget | undefined;
    set_drawBudget: Dispatch<SetStateAction<IDrawBudget | undefined>>;
    applicationInsights: ApplicationInsights;
    isAdmin: boolean;
    isEquityMet: boolean;
    isInspectionsEnabled: boolean;
    borrowerName: string;
    mobileScreen: boolean;
    categories: DrawBudgetCategory[];
    isReadOnly?: boolean;
}

export const DrawButtonGroup = ({
    drawBudget,
    set_drawBudget,
    applicationInsights,
    isAdmin,
    isEquityMet,
    isInspectionsEnabled,
    borrowerName,
    mobileScreen,
    categories,
    isReadOnly = false,
}: IDrawButtonGroup): ReactElement => {
    const drawApi = useMemo(() => new DrawApi(applicationInsights), [applicationInsights]);

    const [isBudgetAlertOpen, set_isBudgetAlertOpen] = useState<boolean>(false);

    const onSuppressClientEmailsToggle = async (suppress: boolean) => {
        if (!drawBudget) return;
        try {
            const response = await drawApi.drawBudgetSuppressClientEmails(drawBudget.id, suppress);
            if (response.statusCode !== 200) {
                throw new Error('An unexpected error has occurred. Please try again.');
            }
            set_drawBudget({ ...drawBudget, suppressClientEmails: suppress });
            handleAdminSuccess(`Client email suppression turned ${suppress ? 'on' : 'off'}.`);
        } catch (ex: any) {
            handleAdminError(ex, ex.message);
        }
    };

    return (
        <>
            <div className='draw-button-group'>
                {!mobileScreen && (
                    <AddDrawButton
                        drawBudget={drawBudget}
                        set_drawBudget={set_drawBudget}
                        applicationInsights={applicationInsights}
                        isAdmin={isAdmin}
                        isApproved={drawBudget?.isApproved ?? false}
                        isFunded={drawBudget?.isFunded ?? false}
                        isEquityMet={isEquityMet}
                        isBudgetMaxed={drawBudget?.isBudgetMaxed ?? false}
                        bankAccountLast4Digits={drawBudget?.bankAccountLast4Digits}
                        borrowerName={borrowerName}
                        isReadOnly={isReadOnly}
                    />
                )}
                {isAdmin && (
                    <button
                        id='budgetPdfButton'
                        type='button'
                        className='button primary'
                        onClick={() => {
                            if (!drawBudget) return;
                            drawApi.emailBudgetPdf(drawBudget.id);
                            set_isBudgetAlertOpen(true);
                        }}
                    >
                        Granite PDF
                    </button>
                )}
                {isAdmin && isInspectionsEnabled && (
                    <InspectionButton
                        drawBudget={drawBudget}
                        set_drawBudget={set_drawBudget}
                        isReadOnly={isReadOnly}
                    />
                )}
                <ReceiptButton
                    categories={categories}
                    isAdmin={isAdmin}
                    applicationInsights={applicationInsights}
                />
                {isAdmin &&
                    (drawBudget?.loanTypeId == LoanType.SubNoteConstruction ||
                        drawBudget?.loanTypeId == LoanType.SubNoteFixFlip) && (
                        <PostClosingButton
                            loanId={drawBudget?.loanId}
                            loanPropertyId={drawBudget?.loanPropertyId}
                            applicationInsights={applicationInsights}
                        />
                    )}
                {isAdmin && (
                    <>
                        Suppress Client Emails
                        <ToggleButton
                            id='suppressClientEmailsToggle'
                            items={[
                                { id: 'On', text: 'On', disabled: isReadOnly },
                                { id: 'Off', text: 'Off', disabled: isReadOnly },
                            ]}
                            selectedItems={[drawBudget?.suppressClientEmails ? 'On' : 'Off']}
                            selectionMode='single'
                            stylingMode='outlined'
                            labelSize={ToggleButtonLabelSize.Small}
                            onItemClick={(e) => onSuppressClientEmailsToggle(e.itemData?.id === 'On')}
                        />
                    </>
                )}
            </div>
            {isBudgetAlertOpen && (
                <Alert
                    onConfirm={() => set_isBudgetAlertOpen(false)}
                    title='Generating Report'
                    message='A PDF will be created and emailed to your address.'
                />
            )}
        </>
    );
};
